import React, { useState } from 'react';
import { ProficiencyLevelRings, ProficiencyCompositeBar } from './ProficiencyLevelRings';
import { getCanDo, getLevelName, getWidaColor } from '@/utils/proficiencyHelpers';
import { TrendingUp, TrendingDown, Minus, X } from 'lucide-react';

/**
 * StudentSnapshot — Click a student name to see a quick popover with their WIDA profile.
 * @param {string} name - Student name
 * @param {object} levels - { listening: 3, speaking: 2, reading: 4, writing: 1 }
 * @param {number} compositeLevel - Overall WIDA level
 * @param {number} growth - Score change since last window (+/- number)
 * @param {string} className - Additional CSS classes
 */
const StudentSnapshot = ({ name, levels = {}, compositeLevel = 0, growth = null, children }) => {
  const [open, setOpen] = useState(false);

  const hasData = compositeLevel > 0;

  return (
    <div className="relative inline-block">
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        className="text-left hover:text-[#002FA7] transition-colors cursor-pointer"
        data-testid={`snapshot-trigger-${name?.replace(/\s/g, '-')}`}
      >
        {children || <span className="font-medium underline decoration-dotted underline-offset-2">{name}</span>}
      </button>

      {open && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />

          {/* Popover */}
          <div
            className="absolute z-50 left-0 top-full mt-2 w-80 bg-white border-2 border-gray-200 shadow-xl p-4 animate-in fade-in slide-in-from-top-2 duration-150"
            data-testid={`snapshot-card-${name?.replace(/\s/g, '-')}`}
          >
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-bold text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>{name}</h4>
              <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X size={14} />
              </button>
            </div>

            {!hasData ? (
              <div className="text-center py-4 text-sm text-gray-400">
                No scored assessments yet
              </div>
            ) : (
              <>
                {/* Composite level */}
                <ProficiencyCompositeBar level={compositeLevel} />

                {/* Growth indicator */}
                {growth !== null && (
                  <div className="flex items-center gap-1.5 mt-2 text-xs">
                    {growth > 0 ? (
                      <><TrendingUp size={14} className="text-green-600" /> <span className="text-green-600 font-semibold">+{growth} points growth</span></>
                    ) : growth < 0 ? (
                      <><TrendingDown size={14} className="text-red-600" /> <span className="text-red-600 font-semibold">{growth} points</span></>
                    ) : (
                      <><Minus size={14} className="text-gray-400" /> <span className="text-gray-400">No change</span></>
                    )}
                  </div>
                )}

                {/* Modality rings */}
                <div className="mt-4 mb-3">
                  <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Modality Profile</div>
                  <ProficiencyLevelRings levels={levels} size={44} />
                </div>

                {/* Strongest Can Do */}
                {(() => {
                  const modalities = ['listening', 'speaking', 'reading', 'writing'];
                  const strongest = modalities.reduce((best, m) =>
                    (levels[m] || 0) > (levels[best] || 0) ? m : best, modalities[0]);
                  const strongLevel = levels[strongest] || 1;
                  const color = getWidaColor(strongLevel);
                  return (
                    <div className={`mt-1 p-2.5 ${color.bg} border ${color.border}`}>
                      <div className="text-[10px] font-semibold text-gray-500 uppercase mb-1">
                        Strongest: {strongest}
                      </div>
                      <p className={`text-xs ${color.text} font-medium leading-relaxed`}>
                        {getCanDo(strongest, strongLevel)}
                      </p>
                    </div>
                  );
                })()}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default StudentSnapshot;
